import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../Components/Layout";
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getUserPackagesLog } from "../redux/actions/userActions";
import useCallOnce from "../hooks/useCallOnce";

function PackageLogs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get userId from location state or URL params
  const userId = location.state?.userId || new URLSearchParams(location.search).get('userId');
  const packageNameFromState = location.state?.packageName || '';
  
  const { data: packagesLog, loading } = useSelector((s) => s.userPackagesLog);
  
  // Local state for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load user packages log for the selected user
  useCallOnce("user/getUserPackagesLog", () => {
    if (userId) dispatch(getUserPackagesLog(userId));
  }, [dispatch, userId]);

  // Refetch on demand
  const handleRefresh = () => {
    if (userId) dispatch(getUserPackagesLog(userId));
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status color for chip
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "default";
      case "pending":
        return "warning";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };

  // Extract logs data
  const logsData = packagesLog?.data || packagesLog?.logs || [];

  return (
    <Layout>
      <Box sx={{ p: { xs: 0, sm: 0 }, width: '100%', minWidth: 0 }}>
        
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          {/* Left Side - Back Button and Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Tooltip title="Go Back to Packages">
              <IconButton 
                onClick={() => navigate(-1)}
                sx={{ 
                  backgroundColor: "#f3f4f6", 
                  "&:hover": { backgroundColor: "#e5e7eb" } 
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {packageNameFromState ? `${packageNameFromState} - ` : ''}Package Logs
            </Typography>
          </Box>

          {/* Right Side - Refresh Button */}
          <Box>
            {/* <Tooltip title="Refresh Data">
              <IconButton 
                onClick={handleRefresh}
                disabled={loading}
                sx={{ 
                  backgroundColor: "#f3f4f6", 
                  "&:hover": { backgroundColor: "#e5e7eb" },
                  "&:disabled": { backgroundColor: "#f9fafb" }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip> */}
          </Box>
        </Box>

          {/* Table Container */}
        <Paper
          sx={{
            borderRadius: 2,
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ color: "#6b7280" }}>
                Loading package logs...
              </Typography>
            </Box>
          ) : logsData.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        Package Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        Price
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        Cycle
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        Calls Included
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        Cost per Call
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        Note
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        Start Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                        End Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logsData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((log, index) => (
                        <TableRow 
                          key={log.id || index}
                          sx={{ 
                            "&:hover": { backgroundColor: "#f9fafb" },
                            "&:last-child td, &:last-child th": { border: 0 }
                          }}
                        >
                          {(() => {
                            const pkg = log.package || {};
                            const name = pkg.name || pkg.package_name || log.package_name || log.name || 'N/A';
                            const price = (pkg.price !== undefined ? pkg.price : log.price);
                            const cycle = pkg.cycle || log.cycle || 'N/A';
                            const callsIncluded = pkg.calls_included || log.calls_included;
                            const costPerCall = (pkg.cost_per_call !== undefined ? pkg.cost_per_call : log.cost_per_call);
                            const isActive = (pkg.is_active !== undefined ? pkg.is_active : log.is_active);
                            const note = log.meta?.note || '';
                            return (
                              <>
                                <TableCell sx={{ color: "#111827" }}>{name}</TableCell>
                                <TableCell sx={{ color: "#111827" }}>{price !== undefined ? `$${price}` : 'N/A'}</TableCell>
                                <TableCell sx={{ color: "#111827" }}>{cycle}</TableCell>
                                <TableCell sx={{ color: "#111827" }}>{callsIncluded ?? 'N/A'}</TableCell>
                                <TableCell sx={{ color: "#111827" }}>{costPerCall !== undefined ? `$${costPerCall}` : 'N/A'}</TableCell>
                                <TableCell sx={{ color: "#6b7280" }}>{note || '-'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={isActive ? 'Active' : 'Inactive'}
                                    color={getStatusColor(isActive ? 'active' : 'inactive')}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                              </>
                            );
                          })()}
                          <TableCell sx={{ color: "#111827" }}>
                            {formatDate(log.start_date || log.created_at)}
                          </TableCell>
                          <TableCell sx={{ color: "#111827" }}>
                            {formatDate(log.end_date || log.expires_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={logsData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                }}
              />
            </>
          ) : (
            // Empty state
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 8,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "#6b7280", mb: 1 }}
              >
                No package logs found
              </Typography>
              <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                This user doesn't have any package history
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Layout>
  );
}

export default PackageLogs;
